import React from "react";
import { ArrowUp, MessageCircle } from "lucide-react";
import AvatarRing from "ui/AvatarRing";
import { getShareUiStrings, resolveShareLocale } from "i18n/shareUi";
import { getQuizUiStrings, resolveQuizLocale } from "i18n/quizUi";
import type { SharedEntityMeta } from "types/chat";
import { getActorInitials, resolveActorAvatarUrl } from "util/actorIdentity";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import { getSharedEntityKindLabel } from "util/sharedEntity";

interface ChatContentCardProps {
  sharedEntity: SharedEntityMeta;
  onOpen?: (sharedEntity: SharedEntityMeta) => void;
  className?: string;
  /**
   * Optional context label rendered above the card (e.g. "Replied to
   * alex's story"). Used by the stories v2 reply flow so that DM
   * recipients see the provenance of the shared content.
   */
  contextLabel?: string;
}

/**
 * Resolve the public avatar URL for the entity author. We delegate to
 * the standardized actor resolver so this component cannot drift from
 * `<ActorAvatar>` on CDN/key handling. `SharedEntityMeta` does not
 * carry `user_id`, so we synthesize a minimal actor for the resolver.
 */
const getAuthorAvatarUrl = (sharedEntity: SharedEntityMeta): string | null => {
  const username = sharedEntity.author_username?.trim() ?? "";
  return resolveActorAvatarUrl({
    id: 0,
    kind: "user",
    username,
    displayName: null,
    avatarKey: sharedEntity.author_avatar_key ?? null,
  });
};

const ChatContentCard: React.FC<ChatContentCardProps> = ({
  sharedEntity,
  onOpen,
  className = "",
  contextLabel,
}) => {
  const ui = getShareUiStrings(resolveShareLocale());
  const quizUi = getQuizUiStrings(resolveQuizLocale());
  const kindLabel = getSharedEntityKindLabel(sharedEntity.entity_type);
  const authorAvatarUrl = getAuthorAvatarUrl(sharedEntity);
  const authorName = sharedEntity.author_username?.trim() || kindLabel;

  return (
    <div className={`flex w-full max-w-[320px] flex-col gap-1 ${className}`}>
      {contextLabel ? (
        <span className="px-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {contextLabel}
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => onOpen?.(sharedEntity)}
        aria-label={ui.viewSharedContent}
        className="block w-full overflow-hidden rounded-3xl border border-eoe-border-main bg-white text-left shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal"
      >
      {sharedEntity.image_url ? (
        <div className="aspect-[16/9] w-full overflow-hidden bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover">
          <img
            src={sharedEntity.image_url}
            alt={sharedEntity.title}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className="flex aspect-[16/9] w-full items-end bg-gradient-to-br from-soc-purple/20 via-eoe-blue/20 to-transparent p-4 dark:from-soc-purple/30 dark:via-eoe-blue/30">
          <span className="rounded-full bg-white/85 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-eoe-text dark:bg-eoe-dark-bg-modal/85 dark:text-eoe-dark-text">
            {kindLabel}
          </span>
        </div>
      )}

      <div className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-eoe-bg-hover px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-eoe-text-sub dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text-sub">
            {kindLabel}
          </span>
          <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {ui.sharedContentLabel}
          </span>
        </div>

        <div>
          <div className="line-clamp-2 text-base font-semibold text-eoe-text dark:text-eoe-dark-text">
            {sharedEntity.title}
          </div>
          {sharedEntity.description ? (
            <div className="mt-1 line-clamp-3 text-sm leading-6 text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {sharedEntity.description}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <AvatarRing
              size={ACTOR_AVATAR_SIZES.message}
              ringState="none"
              imageUrl={authorAvatarUrl}
              imageAlt={authorName}
              initials={getActorInitials({
                id: 0,
                kind: "user",
                username: authorName,
                displayName: authorName,
                avatarKey: null,
              })}
            />
            <span className="truncate text-sm text-eoe-text dark:text-eoe-dark-text">
              {authorName}
            </span>
          </div>

          <div className="flex items-center gap-3 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {typeof sharedEntity.vote_count === "number" ? (
              <span className="inline-flex items-center gap-1">
                <ArrowUp className="size-3" />
                {sharedEntity.vote_count}
              </span>
            ) : null}
            {typeof sharedEntity.comment_count === "number" ? (
              <span className="inline-flex items-center gap-1">
                <MessageCircle className="size-3" />
                {sharedEntity.comment_count}
              </span>
            ) : null}
          </div>
        </div>

        {sharedEntity.entity_type === "quiz" ? (
          <div className="flex justify-end">
            <span className="rounded-full bg-eoe-blue/10 px-3 py-1 text-xs font-semibold text-eoe-blue dark:bg-eoe-blue/20">
              {quizUi.viewQuizAction}
            </span>
          </div>
        ) : null}
        </div>
      </button>
    </div>
  );
};

export default ChatContentCard;
